import { useState, useEffect } from "react";
import { Header } from "../common/Header";
import { useNavigate, useSearchParams } from "react-router";
import { OTPInput } from "./OTPInput";
import { OTPTimer } from "./OTPTimer";
import { OTPActions } from "./OTPActions";
import { setToken, verifyOtp, isAuthenticated } from "@nusrif/services";
import { isUserOnboarded, setUser } from "@nusrif/services";
import { clearHistoryAndNavigate } from "@nusrif/utils";
import { createCart } from "@nusrif/services";
import { BottomSheet } from "../ui/bottom-sheet";
import { OnboardingForm } from "./OnboardingForm";
export const OTPVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [attemptCount, setAttemptCount] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [showOnboardingForm, setShowOnboardingForm] = useState(false);
  // Get contact info and return URL from URL params
  const contactInfo = searchParams.get("contact") || "";
  const returnUrl = searchParams.get("returnUrl") || "/";
  const debugCode = searchParams.get("code") || "";
  const MAX_ATTEMPTS = 3;
  const BLOCK_DURATION = 300; // 5 minutes in seconds
  // Check if user is already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      // Clear history and navigate to prevent back navigation
      clearHistoryAndNavigate(navigate, returnUrl);
    }
  }, [navigate, returnUrl]);
  // Timer countdown
  useEffect(() => {
    if (timeLeft > 0 && !canResend) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setCanResend(true);
    }
  }, [timeLeft, canResend]);
  const handleVerifyOTP = async () => {
    // Prevent double execution
    if (isVerifying) {
      return;
    }
    if (isBlocked) {
      setError("Too many failed attempts. Please try again later.");
      return;
    }
    if (otp.length !== 6) {
      setError("Please enter the complete 6-digit OTP");
      return;
    }
    setIsVerifying(true);
    setError("");
    try {
      const response = await verifyOtp({
        code: otp,
        otpType: "LOGIN",
        phoneNumber: contactInfo,
      });
      // Check if OTP is valid
      const isValidOTP = response.success && response.data.verified;
      if (isValidOTP) {
        // Set user and token
        if (response.success && response.data.user) {
          setUser(response.data.user);
        }
        if (response.success && response.data.token) {
          setToken(response.data.token);
        }
        // Clear attempt count on success
        localStorage.removeItem("otpAttemptCount");
        localStorage.removeItem("otpBlockEndTime");
        console.log("OTP verified successfully");
        // Clear history and navigate to return URL to prevent back navigation
        if (!isUserOnboarded()) {
          setShowOnboardingForm(true);
          return;
        }
        // Create cart
        if (response.success && !response.data.user?.cartId) {
          await createCart();
        }
        // If user is onboarded, navigate to the return URL
        clearHistoryAndNavigate(navigate, returnUrl);
      } else {
        const newAttemptCount = attemptCount + 1;
        setAttemptCount(newAttemptCount);
        localStorage.setItem("otpAttemptCount", newAttemptCount.toString());
        if (newAttemptCount >= MAX_ATTEMPTS) {
          // Block user for 5 minutes
          const blockEndTime = Date.now() + BLOCK_DURATION * 1000;
          localStorage.setItem("otpBlockEndTime", blockEndTime.toString());
          setIsBlocked(true);
          setTimeLeft(BLOCK_DURATION);
          setError(
            `Too many failed attempts. Please try again after ${
              BLOCK_DURATION / 60
            } minutes.`
          );
        } else {
          setError(
            `Invalid OTP. ${
              MAX_ATTEMPTS - newAttemptCount
            } attempt(s) remaining.`
          );
        }
        // Clear OTP on error
        setOtp("");
      }
    } catch (error) {
      console.error("OTP verification failed:", error);
      setError("Verification failed. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };
  // Removed auto-submit to prevent double execution with manual button click
  // Check if user is blocked
  useEffect(() => {
    const blockEndTime = localStorage.getItem("otpBlockEndTime");
    if (blockEndTime) {
      const now = Date.now();
      const blockEnd = parseInt(blockEndTime);
      if (now < blockEnd) {
        setIsBlocked(true);
        const remainingTime = Math.ceil((blockEnd - now) / 1000);
        setTimeLeft(remainingTime);
      } else {
        localStorage.removeItem("otpBlockEndTime");
        localStorage.removeItem("otpAttemptCount");
      }
    }
    const savedAttempts = localStorage.getItem("otpAttemptCount");
    if (savedAttempts) {
      setAttemptCount(parseInt(savedAttempts));
    }
  }, []);
  const handleResendOTP = async () => {
    if (!canResend || isBlocked) return;
    try {
      // Reset timer
      setTimeLeft(60);
      setCanResend(false);
      setError("");
      setOtp("");
      // Show success message briefly
      setError(""); // Clear any existing errors
    } catch (error) {
      console.error("Failed to resend OTP:", error);
      setError("Failed to resend OTP. Please try again.");
    }
  };
  const handleBackToSignIn = () => {
    navigate("/signin");
  };
  const formatContactInfo = (contact: string): string => {
    return contact;
  };
  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Header */}
      <Header
        title="Verify OTP"
        showBackButton={true}
        showCartButton={false}
        onBackClick={handleBackToSignIn}
      />
      {/* Main Content */}
      <div className="pt-20 px-5">
        <div className="max-w-md mx-auto space-y-8">
          {/* Header Section */}
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-primary-blue rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-black mb-4">
              Verify Your Identity
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              We&apos;ve sent a 6-digit verification code to
            </p>
            <p className="text-black font-semibold text-lg mt-2">
              {formatContactInfo(contactInfo)}
            </p>
          </div>
          {/* OTP Form */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-2">
            {/* OTP Input */}
            <OTPInput
              value={otp}
              onChange={setOtp}
              disabled={isVerifying || isBlocked}
              error={!!error}
            />
            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <svg
                  className="w-5 h-5 text-red-500 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
            {/* Timer and Resend */}
            <OTPTimer
              timeLeft={timeLeft}
              canResend={canResend}
              onResend={handleResendOTP}
              isBlocked={isBlocked}
            />
            {/* Action Buttons */}
            <OTPActions
              onVerify={handleVerifyOTP}
              onBackToSignIn={handleBackToSignIn}
              isVerifying={isVerifying}
              disabled={otp.length !== 6 || isBlocked}
            />
          </div>
          {debugCode && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              {" "}
              <p className="text-yellow-800 text-sm">
                {" "}
                <strong>Debug:</strong> Use OTP &quot;{debugCode}&quot; for testing{" "}
              </p>{" "}
            </div>
          )}
          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Security Notice</p>
                <p>
                  Never share your OTP with anyone. Our team will never ask for
                  your verification code. The code expires in 10 minutes.
                </p>
              </div>
            </div>
          </div>
          <BottomSheet
            isOpen={showOnboardingForm}
            onClose={() => {
              setShowOnboardingForm(true);
            }}
            title="Welcome! Let's set up your profile"
            disableCloseButton={true}
          >
            <OnboardingForm />
          </BottomSheet>
        </div>
      </div>
    </div>
  );
};
